/**
 * O histórico de versões de uma automação, no painel lateral.
 *
 * Toda alteração vira uma versão nova no serviço, e restaurar cria outra a
 * partir da antiga: nada some. O painel mostra isso e deixa restaurar, com a
 * mesma régua do backend para quem pode. "Ver como estava" abre o editor do
 * mapa, e entra junto com ele.
 *
 * O desenho é o do protótipo v2: o ponto da versão no ar com o ✓, o selo "No
 * ar" vivo, a data exata com a idade ao lado, e as versões entrando em ordem.
 * O protótipo mostrava também a mudança campo a campo, o nome de quem alterou
 * e a revisão da Cliqfy; o histórico do serviço não traz nada disso (só a
 * ação e o `autor_id`), e a tela não inventa.
 */

import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import { useApi, useHistoricoDaAutomacao } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Dialogo, Icone, Selo } from '@/shared/ui';
import { Estado } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import {
  linhasQueRestauram,
  oQueAconteceu,
  podeRestaurar,
  quando,
  quemFez,
  type Automacao,
} from '../lib/automacoes';
import './historico-da-automacao.css';

export function HistoricoDaAutomacao({
  automacao: a,
  nome,
  papel,
  meuId,
  aoFechar,
  aoVerVersao,
}: {
  automacao: Automacao;
  nome: string;
  papel: string | null;
  meuId: string | null;
  aoFechar: () => void;
  /** Abre o mapa de uma versão (só para ler), ou o de agora com `null`. */
  aoVerVersao?: (versao: number | null) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const historico = useHistoricoDaAutomacao(a.id);
  const descricao = useId();
  const [confirmando, definirConfirmando] = useState<number | null>(null);
  const [feito, definirFeito] = useState<string | null>(null);

  const restaurar = useMutation({
    mutationFn: (versao: number) =>
      api!.restaurarAutomacao({ caminho: { id: a.id }, corpo: { versao } }),
    onMutate: () => definirFeito(null),
    onSuccess: (_r, versao) => {
      definirConfirmando(null);
      definirFeito(
        a.situacao === 'ativa'
          ? `Versão nova criada a partir da v${versao}. A que está no ar continua; a nova está em teste, esperando ativar.`
          : `Versão nova criada a partir da v${versao}, como rascunho.`,
      );
    },
    // O histórico e a lista mudam juntos: a versão nova aparece nos dois.
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.automacoes() }),
  });

  return (
    <Dialogo
      titulo="Histórico"
      forma="lateral"
      descricaoId={descricao}
      aoFechar={() => {
        if (!restaurar.isPending) aoFechar();
      }}
      botoes={
        <Botao
          variante="secundario"
          onClick={aoFechar}
          desabilitado={restaurar.isPending}
          motivo="Restaurando…"
        >
          Fechar
        </Botao>
      }
    >
      <div className="historico-da-automacao">
        <p className="historico-da-automacao__apoio" id={descricao}>
          {nome}
          {historico.data && ` · ${versoesEmPalavras(historico.data.itens)}`}, da mais nova para a
          mais antiga.
        </p>

        <div className="historico-da-automacao__regra">
          <span className="historico-da-automacao__regra-icone" aria-hidden="true">
            <Icone nome="identidade" />
          </span>
          <div>
            <p className="historico-da-automacao__regra-titulo">Nada some daqui.</p>
            <p>
              Toda alteração vira uma versão nova, e restaurar cria outra a partir da antiga: o
              histórico nunca é apagado nem escrito por cima.
            </p>
          </div>
        </div>

        {/* A região viva existe antes do recado, para o leitor de tela anunciar. */}
        <div role="status" aria-live="polite">
          {feito && <p className="historico-da-automacao__feito">{feito}</p>}
        </div>

        {restaurar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(restaurar.error, {
              generica: 'Não foi possível restaurar agora. Nada mudou; tente de novo.',
              porCodigo: {
                ENGINE_INDISPONIVEL:
                  'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
                REQUISICAO_INVALIDA:
                  'Esta versão não vale mais como está: ela usa algo que mudou desde então.',
                NAO_ENCONTRADO:
                  'Esta versão usa algo que não existe mais, como uma pessoa ou uma lista.',
                SEM_PERMISSAO: 'O seu papel não pode restaurar esta versão.',
              },
            })}
          </Faixa>
        )}

        <Estado
          consulta={historico}
          forma="lista"
          linhas={4}
          vazio={{
            quando: (d) => d.itens.length === 0,
            titulo: 'Sem histórico ainda',
            porque: 'As versões aparecem aqui a partir da primeira alteração.',
          }}
        >
          {(dados) => {
            const restauraveis = linhasQueRestauram(dados.itens, a.versao);
            // A versão de agora tem várias linhas (editada, depois ativada): a
            // marca vai só na mais nova delas.
            const daVersaoDeAgora = dados.itens.findIndex((v) => v.versao === a.versao);
            // Uma versão mais nova que a de agora numa automação no ar: é a que
            // foi editada (ou restaurada) e espera teste. A marca vai na linha mais nova dela.
            const maisNova = dados.itens[0];
            const esperando =
              a.situacao === 'ativa' && maisNova !== undefined && maisNova.versao > a.versao;
            return (
              <ol className="historico-da-automacao__linha-do-tempo">
                {dados.itens.map((v, i) => {
                  const atual = i === daVersaoDeAgora;
                  const noAr = atual && a.situacao === 'ativa';
                  const oferece =
                    restauraveis.has(i) && v.foto !== null && podeRestaurar(a, papel, v.foto);
                  const podeVer =
                    aoVerVersao !== undefined && v.foto !== null && (atual || restauraveis.has(i));
                  return (
                    <li
                      key={`${v.versao}-${v.em}-${i}`}
                      className="historico-da-automacao__versao"
                      data-no-ar={noAr ? 'sim' : undefined}
                      style={{ '--i': Math.min(i, 6) } as CSSProperties}
                    >
                      <span className="historico-da-automacao__ponto" aria-hidden="true">
                        {noAr && <Icone nome="check" />}
                      </span>
                      <div className="historico-da-automacao__cartao">
                        <p className="historico-da-automacao__topo">
                          <span
                            className="historico-da-automacao__numero"
                            data-no-ar={noAr ? 'sim' : undefined}
                          >
                            v{v.versao}
                          </span>
                          {noAr && (
                            <Selo tom="ok" vivo>
                              No ar
                            </Selo>
                          )}
                          {atual && !noAr && <Selo tom="neutro">A de agora</Selo>}
                          {esperando && i === 0 && (
                            <Selo tom="atencao">Mais nova, ainda não está no ar</Selo>
                          )}
                          <span className="historico-da-automacao__quando">
                            <time dateTime={v.em}>{quando(v.em)}</time>
                            {idadeEmPalavras(v.em) && ` · ${idadeEmPalavras(v.em)}`}
                          </span>
                        </p>
                        <p className="historico-da-automacao__o-que">{oQueAconteceu(v)}</p>
                        <p className="historico-da-automacao__quem">{quemFez(v, meuId)}</p>

                        {oferece && confirmando === v.versao ? (
                          <div className="historico-da-automacao__confirmar">
                            <p>
                              <strong>Restaurar a v{v.versao}?</strong>{' '}
                              {a.situacao === 'ativa'
                                ? `Vou criar uma versão nova com o conteúdo da v${v.versao}. A que está no ar continua rodando; a nova fica em teste até alguém ativar.`
                                : `Vou criar uma versão nova com o conteúdo da v${v.versao}, como rascunho. As outras versões continuam no histórico, do jeito que estão.`}
                            </p>
                            <div className="historico-da-automacao__botoes">
                              <Botao
                                variante="fantasma"
                                miudo
                                onClick={() => definirConfirmando(null)}
                                desabilitado={restaurar.isPending}
                                motivo="Restaurando…"
                              >
                                Cancelar
                              </Botao>
                              <Botao
                                variante="primario"
                                miudo
                                onClick={() => restaurar.mutate(v.versao)}
                                desabilitado={restaurar.isPending}
                                motivo="Restaurando…"
                              >
                                {restaurar.isPending
                                  ? 'Restaurando…'
                                  : `Criar a partir da v${v.versao}`}
                              </Botao>
                            </div>
                          </div>
                        ) : (
                          (podeVer || oferece) && (
                            <div className="historico-da-automacao__botoes">
                              {podeVer && (
                                <Botao
                                  variante="fantasma"
                                  miudo
                                  onClick={() => aoVerVersao!(atual ? null : v.versao)}
                                >
                                  <Icone nome="olho" />
                                  {atual ? 'Ver o mapa' : 'Ver como estava'}
                                </Botao>
                              )}
                              {oferece && (
                                <Botao
                                  variante="secundario"
                                  miudo
                                  onClick={() => definirConfirmando(v.versao)}
                                  desabilitado={restaurar.isPending}
                                  motivo="Restaurando outra versão…"
                                >
                                  <Icone nome="repetir" />
                                  Restaurar esta versão
                                </Botao>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            );
          }}
        </Estado>
      </div>
    </Dialogo>
  );
}

/** "1 versão", "4 versões": contadas pelo número, não pelas linhas (uma versão tem várias). */
function versoesEmPalavras(itens: readonly { versao: number }[]): string {
  const n = new Set(itens.map((v) => v.versao)).size;
  return n === 1 ? '1 versão' : `${n} versões`;
}
